import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { giftCardsApi } from '../api/giftCards'
import Boton from '../components/Boton'
import Paginador from '../components/Paginador'
import { useAuth } from '../context/AuthContext'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, text, sx } from '../design/tokens'

/**
 * Gift cards HEREDADAS de Magento (Martin, 26 sep 2026): las 1.982 con saldo
 * que quedaron en la tienda vieja, importadas a LGR. Solo las ve el super
 * administrador. "Habilitada" = el cliente puede canjearla en el checkout;
 * cuando la usa, LGR la acepta sin que él lo note, le pide aprobación a
 * Martin por Telegram y la NV queda retenida hasta que apruebe.
 */
const FILTROS = [
  ['todas', 'Todas'],
  ['habilitadas', 'Habilitadas'],
  ['deshabilitadas', 'Deshabilitadas'],
  ['vencidas', 'Vencidas'],
  ['usadas', 'Usadas'],
]

// Estado tal como lo tenía Magento (Amasty): 1 activa · 2 vencida · 0 inactiva · 3 otro.
const ESTADO_MAGENTO = {
  1: { label: 'activa', bg: colors.okBg, color: colors.ok },
  2: { label: 'vencida', bg: colors.errBg, color: colors.err },
  0: { label: 'inactiva', bg: colors.bgSoft, color: colors.textSec },
  3: { label: 'otro', bg: colors.warnBg, color: colors.warn },
}

const APROBACION = {
  pendiente: { label: 'pendiente', bg: colors.warnBg, color: colors.warn },
  aprobada: { label: 'aprobada', bg: colors.okBg, color: colors.ok },
  rechazada: { label: 'rechazada', bg: colors.errBg, color: colors.err },
}

function Badge({ def }) {
  if (!def) return null
  return <span style={{ ...st.badge, background: def.bg, color: def.color }}>{def.label}</span>
}

function Kpi({ label, valor }) {
  return (
    <div style={{ ...sx.card, padding: '12px 16px', flex: 1, minWidth: 150 }}>
      <div style={st.kpiLabel}>{label}</div>
      <div style={st.kpiValor}>{valor}</div>
    </div>
  )
}

export default function GiftCardsHeredadasPage() {
  const navigate = useNavigate()
  const { isSuperAdmin, impersonateRole } = useAuth()
  const puedeVer = isSuperAdmin && !impersonateRole

  const [filtro, setFiltro] = useState('todas')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ data: [], total: 0, per_page: 50, current_page: 1, resumen: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cambiando, setCambiando] = useState(null)

  // La carga vive adentro del efecto y todo setState va DESPUÉS del fetch
  // (mismo patrón que la landing de pago): sin "Cargando…" sincrónico.
  useEffect(() => {
    if (!puedeVer) return undefined
    let vivo = true
    ;(async () => {
      try {
        const { data: res } = await giftCardsApi.heredadas({ q: q || undefined, filtro, page })
        if (vivo) { setData(res); setError(null) }
      } catch (err) {
        if (vivo) setError(err.response?.data?.message || 'No se pudieron cargar las gift cards heredadas.')
      } finally {
        if (vivo) setLoading(false)
      }
    })()
    return () => { vivo = false }
  }, [puedeVer, q, filtro, page])

  const habilitar = async (g, habilitada) => {
    setCambiando(g.id)
    try {
      const { data: fila } = await giftCardsApi.habilitarHeredada(g.id, habilitada)
      setData(d => ({ ...d, data: d.data.map(x => (x.id === g.id ? { ...x, ...fila } : x)) }))
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo cambiar.')
    } finally {
      setCambiando(null)
    }
  }

  if (!puedeVer) {
    return (
      <div>
        <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
        <div style={st.aviso}>Las gift cards heredadas de Magento solo las ve el super administrador.</div>
      </div>
    )
  }

  const r = data.resumen
  const totalPaginas = Math.max(1, Math.ceil((data.total || 0) / (data.per_page || 50)))
  const hoy = new Date().toISOString().slice(0, 10)
  const vencida = (g) => !!g.vence_el && g.vence_el < hoy

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Gift cards heredadas de Magento</h1>
          {r && (
            <div style={st.sub}>
              {r.total} cards · saldo total $ {fmtMonto(r.saldo_total)} · {r.habilitadas} habilitadas por $ {fmtMonto(r.saldo_habilitadas)} · {r.vencidas} vencidas · {r.usadas} usadas
            </div>
          )}
        </div>
        <Boton variante="ghost" sm onClick={() => navigate('/gift-cards')}>← Gift cards</Boton>
      </div>
      <p style={st.explicacion}>
        Solo las ve el super administrador. <b>Habilitada</b> = el cliente puede canjearla en la tienda; cuando la usa, te llega un pedido de aprobación por Telegram y la NV queda retenida hasta que la apruebes.
      </p>

      {r && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Kpi label="Saldo total" valor={`$ ${fmtMonto(r.saldo_total)}`} />
          <Kpi label="Habilitadas" valor={`${r.habilitadas} · $ ${fmtMonto(r.saldo_habilitadas)}`} />
          <Kpi label="Vencidas" valor={r.vencidas} />
          <Kpi label="Usadas" valor={r.usadas} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          placeholder="Buscar por código o cuenta Magento…"
          value={q}
          onChange={e => { setQ(e.target.value); setPage(1) }}
          style={st.input}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTROS.map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => { setFiltro(v); setPage(1) }}
              style={{ ...st.chip, ...(filtro === v ? st.chipActivo : {}) }}
            >{label}</button>
          ))}
        </div>
        {(q || filtro !== 'todas') && (
          <Boton variante="ghost" sm onClick={() => { setQ(''); setFiltro('todas'); setPage(1) }}>Limpiar</Boton>
        )}
      </div>

      {error && <div style={st.error}>{error}</div>}

      <div style={st.tableWrap}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr style={{ background: colors.bgSoft }}>
              {['Código', 'Cuenta Magento', 'Inicial', 'Saldo', 'Vence', 'Estado Magento', 'Habilitada', 'Uso'].map(h => (
                <th key={h} style={{ ...sx.th, textAlign: ['Inicial', 'Saldo'].includes(h) ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && data.data.length === 0 && (
              <tr><td colSpan={8} style={st.vacio}>Cargando…</td></tr>
            )}
            {!loading && data.data.length === 0 && (
              <tr><td colSpan={8} style={st.vacio}>No hay gift cards heredadas que coincidan.</td></tr>
            )}
            {data.data.map(g => (
              <tr key={g.id} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                <td style={{ ...st.td, ...sx.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{g.codigo}</td>
                <td style={{ ...st.td, ...sx.mono, color: colors.textSec }}>{g.magento_account_id || '—'}</td>
                <td style={{ ...st.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>$ {fmtMonto(g.monto_inicial)}</td>
                <td style={{ ...st.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>$ {fmtMonto(g.saldo)}</td>
                <td style={{ ...st.td, ...sx.mono, whiteSpace: 'nowrap', color: vencida(g) ? colors.err : colors.textSec }}>
                  {g.vence_el ? fmtFecha(g.vence_el) : '—'}
                </td>
                <td style={st.td}><Badge def={ESTADO_MAGENTO[g.estado_magento]} /></td>
                <td style={st.td}>
                  {g.habilitada ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Badge def={{ label: 'sí', bg: colors.okBg, color: colors.ok }} />
                      <Boton variante="ghost" sm disabled={cambiando === g.id} onClick={() => habilitar(g, false)}>Deshabilitar</Boton>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Badge def={{ label: 'no', bg: colors.bgSoft, color: colors.textSec }} />
                      <Boton variante="secondary" sm disabled={cambiando === g.id} onClick={() => habilitar(g, true)}>Habilitar</Boton>
                    </span>
                  )}
                </td>
                <td style={st.td}>
                  {g.usada_en_pedido && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ ...sx.mono }}>{g.usada_en_pedido}</span>
                      <Badge def={APROBACION[g.aprobacion]} />
                      {g.usada_at && <span style={{ fontSize: text.sec, color: colors.textMeta }}>{fmtFecha(g.usada_at)} {fmtHoraDe(g.usada_at)}</span>}
                    </span>
                  )}
                  {/* Convertida en gift card de LGR: su saldo real y las NV donde se canjeó. */}
                  {g.gift_card ? (
                    <div style={{ fontSize: text.sec, color: colors.textSec }}>
                      Saldo en LGR <b style={{ ...sx.mono }}>$ {fmtMonto(g.gift_card.saldo)}</b> · {g.gift_card.estado}
                      {(g.gift_card.canjes || []).map(c => (
                        <div key={c.id} style={{ ...sx.mono }}>
                          {c.nota_venta?.numero_lgr || `NV #${c.nota_venta_id}`} · $ {fmtMonto(c.monto)} · {c.estado === 'aplicado' ? 'facturado' : 'reservado'}
                        </div>
                      ))}
                    </div>
                  ) : !g.usada_en_pedido && <span style={{ color: colors.textMeta }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador
        pagina={data.current_page}
        totalPaginas={totalPaginas}
        onCambiar={setPage}
        info={`${data.total || 0} gift cards heredadas`}
      />
    </div>
  )
}

const st = {
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: 4 },
  explicacion: { fontSize: text.sec, color: colors.textSec, margin: '0 0 14px' },
  aviso: { marginTop: 12, padding: '12px 16px', background: colors.warnBg, border: `1px solid ${colors.warnStrong}`, borderRadius: 8, color: colors.warn, fontSize: text.body },
  error: { marginBottom: 12, padding: '10px 14px', background: colors.errBg, color: colors.err, borderRadius: 8, fontSize: text.sec },
  input: { padding: '7px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: 6, fontSize: text.body, width: 280 },
  chip: {
    padding: '4px 12px', borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.bg,
    color: colors.textSec, fontSize: text.sec, fontWeight: 600, cursor: 'pointer',
  },
  chipActivo: { background: colors.linkBg, color: colors.link, borderColor: colors.link },
  tableWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'auto' },
  td: { padding: '10px 14px', verticalAlign: 'middle' },
  vacio: { padding: 24, textAlign: 'center', color: colors.textMeta },
  badge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: text.meta, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  kpiLabel: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  kpiValor: { fontSize: 22, fontWeight: 700, marginTop: 2, ...sx.mono, color: colors.text },
}
